import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, LogOut, Building2, Wrench } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * DemoModeBanner — appears at the top of EVERY authenticated page when the
 * current user is one of the seeded demo accounts (is_demo_user=true OR
 * email matches the `.demo@procureoneultra.com` pattern).
 *
 * Renders nothing for real users. Read-only awareness UI — does not modify
 * any data; does not change any flows.
 */
export default function DemoModeBanner() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  if (!user) return null;
  const isDemo =
    user.is_demo_user === true ||
    (typeof user.email === 'string' && user.email.endsWith('.demo@procureoneultra.com'));
  if (!isDemo) return null;

  const exitDemo = async () => {
    // Navigate to home FIRST (public page, exempt from 401 redirect),
    // THEN logout — avoids ProtectedRoute race that would otherwise send
    // the user to /login instead of /.
    navigate('/');
    try { await logout(); } catch { /* ignore */ }
  };

  const goRegister = async (role) => {
    // IMPORTANT: navigate to the (public) register page FIRST, THEN logout.
    // If we logout first while still mounted under a <ProtectedRoute>, the
    // protected route emits <Navigate to='/login' replace/> which races and
    // overrides the subsequent navigate() call, dropping the source param.
    navigate(`/${role}-register?source=demo_mode_banner`);
    try { await logout(); } catch { /* ignore */ }
  };

  return (
    <div
      className="w-full bg-orange text-white shadow-md border-b-2 border-gold"
      data-testid="demo-mode-banner"
      role="status"
      aria-label="Demo mode active"
    >
      <div className="container mx-auto px-3 sm:px-6 py-2 flex flex-col sm:flex-row items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-xs sm:text-sm font-bold">
          <Sparkles className="h-4 w-4 shrink-0" />
          <span data-testid="demo-mode-label">Demo Mode Active</span>
          <span className="hidden sm:inline text-white/80 font-normal text-xs">— you are viewing isolated demo data. Nothing you do here affects production.</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-center">
          <button
            onClick={exitDemo}
            className="px-3 py-1 rounded-md bg-white/15 hover:bg-white/25 text-white text-xs font-bold inline-flex items-center transition-colors"
            data-testid="demo-banner-exit-btn"
          >
            <LogOut className="h-3 w-3 mr-1.5" /> Exit Demo
          </button>
          <button
            onClick={() => goRegister('buyer')}
            className="px-3 py-1 rounded-md bg-white text-navy text-xs font-bold inline-flex items-center hover:bg-white/90 transition-colors"
            data-testid="demo-banner-register-buyer-btn"
          >
            <Building2 className="h-3 w-3 mr-1.5" /> Register as Buyer
          </button>
          <button
            onClick={() => goRegister('supplier')}
            className="px-3 py-1 rounded-md bg-navy text-white text-xs font-bold inline-flex items-center hover:bg-navy/90 transition-colors"
            data-testid="demo-banner-register-supplier-btn"
          >
            <Wrench className="h-3 w-3 mr-1.5" /> Register as Supplier
          </button>
        </div>
      </div>
    </div>
  );
}
